import { Injectable, inject } from '@angular/core';
import { AuthService } from '../../auth/auth.service';
import { AutoclassifierSdkService } from '../../autoclassifier-sdk/autoclassifier-sdk.service';
import {
  ITPIntegrationCredentialsResponse,
  ITPIntegrationCredentials
} from '@softdocs/capture-ui-libraries';

/**
 * Service for managing ITP Integration Credentials.
 * Provides methods to retrieve, create, update, and delete credentials.
 */
@Injectable({
  providedIn: 'root'
})
export class ITPIntegrationCredentialsService {
  private authService = inject(AuthService);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);

  private credentialId: string | null = null;

  /**
   * Get ITP Integration Credentials for tenant
   * @returns {Promise<ITPIntegrationCredentialsResponse>} - Credentials for tenant
   */
  async getITPIntegrationCredentials(): Promise<ITPIntegrationCredentialsResponse | null> {
    const accessToken = this.authService.getAuthToken()!;
    try {
      const credentials =
        await this.autoclassifierSdkService.itpIntegrationCredentialService.getITPIntegrationCredentials(
          accessToken
        );
      this.credentialId = credentials?.id || null;
      return credentials;
    } catch (error) {
      this.credentialId = null;
      return null;
    }
  }

  /**
   * Create new ITP Integration Credentials
   * @param {ITPIntegrationCredentials} data - credentials data to create
   * @returns {Promise<void>}
   */
  addITPIntegrationCredentials(data: ITPIntegrationCredentials): Promise<void> {
    const accessToken = this.authService.getAuthToken()!;
    return this.autoclassifierSdkService.itpIntegrationCredentialService.addITPIntegrationCredentials(
      accessToken,
      data
    );
  }

  /**
   * Update existing ITP Integration Credentials
   * @param {string} id - credential ID to update
   * @param {ITPIntegrationCredentials} data - credentials data to update
   * @returns {Promise<ITPIntegrationCredentials>} - Updated credentials
   */
  updateITPIntegrationCredentials(
    id: string,
    data: ITPIntegrationCredentials
  ): Promise<ITPIntegrationCredentials> {
    const accessToken = this.authService.getAuthToken()!;
    return this.autoclassifierSdkService.itpIntegrationCredentialService.updateITPIntegrationCredentials(
      accessToken,
      id,
      data
    );
  }

  /**
   * Delete ITP Integration Credentials
   * @param {string} id - credential ID to delete
   * @returns {Promise<string>} - Deletion confirmation message
   */
  deleteITPIntegrationCredentials(id: string): Promise<string> {
    const accessToken = this.authService.getAuthToken()!;
    return this.autoclassifierSdkService.itpIntegrationCredentialService.deleteITPIntegrationCredentials(
      accessToken,
      id
    );
  }

  /**
   * Manages credentials - creates if not exists, updates if exists
   * @param {ITPIntegrationCredentials} data - credentials data
   * @returns {Promise<void>}
   */
  async manageCredentials(data: ITPIntegrationCredentials): Promise<void> {
    if (this.credentialId) {
      await this.updateITPIntegrationCredentials(this.credentialId, data);
    } else {
      await this.addITPIntegrationCredentials(data);
      // Reload to get the new ID
      await this.getITPIntegrationCredentials();
    }
  }
}
